import React from 'react'
import { useBooksStore } from '../../store/booksStore'

interface WorkbenchErrorBoundaryProps {
  children: React.ReactNode
}

interface WorkbenchErrorBoundaryState {
  componentStack: string
  error: Error | null
  summary: {
    books: number
    chapters: number
    sourceObjects: number
    witnesses: number
  } | null
}

export class WorkbenchErrorBoundary extends React.Component<
  WorkbenchErrorBoundaryProps,
  WorkbenchErrorBoundaryState
> {
  state: WorkbenchErrorBoundaryState = {
    error: null,
    componentStack: '',
    summary: null,
  }

  static getDerivedStateFromError(error: Error): Partial<WorkbenchErrorBoundaryState> {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const store = useBooksStore.getState()
    const summary = {
      books: store.books.length,
      chapters: store.chapters.length,
      witnesses: store.witnesses.length,
      sourceObjects: store.sourceObjects.length,
    }

    this.setState({
      componentStack: info.componentStack || '',
      summary,
    })

    console.error('[WorkbenchErrorBoundary] Workbench render failed.', {
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
      pathname: typeof window !== 'undefined' ? window.location.pathname : '',
      summary,
    })
  }

  render() {
    const { error, componentStack, summary } = this.state

    if (!error) {
      return this.props.children
    }

    return (
      <div
        data-testid="workbench-error-boundary"
        className="min-h-screen bg-paper-0 px-6 py-10 text-ink-0"
      >
        <div className="mx-auto w-full max-w-3xl border border-paper-2 bg-paper-0 p-6 shadow-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-ink-3">Workbench</div>
          <h1 className="mt-2 font-serif text-2xl font-medium text-ink-0">Workbench failed to render</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-ink-3">
            The local workbench data is still intact. No IndexedDB or localStorage data was deleted or reset.
          </p>
          <div className="mt-5 border border-paper-2 bg-paper-1 px-4 py-3 text-sm text-ink-1">
            <div><strong>Error:</strong> {error.message || 'Unknown workbench render error'}</div>
            {summary && (
              <div className="mt-2">
                <strong>Local data:</strong> {summary.books} books, {summary.chapters} chapters, {summary.witnesses} witnesses, {summary.sourceObjects} source objects
              </div>
            )}
          </div>
          {componentStack ? (
            <pre className="mt-4 overflow-x-auto whitespace-pre-wrap border border-paper-2 bg-paper-1 px-4 py-3 text-xs text-ink-2">
              {componentStack.trim()}
            </pre>
          ) : null}
        </div>
      </div>
    )
  }
}

export default WorkbenchErrorBoundary
